import { Trans } from '@lingui/react'
import { IconArrowRight } from '@redteclab/icons'
import classnames from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import {
  TENANT_AT,
  TENANT_CH,
  TENANT_COM,
  TENANT_FR,
  TENANT_IT,
} from 'shared/consts'
import AfterQuerySuggestLayer from 'shared/experiments/components/AfterQuerySuggestLayer'
import ExtendedSearchBoxListItem from 'shared/experiments/components/ExtendedSearchBoxResults/ExtendedSearchBoxListItem'
import { EXTENDED_SEARCHBOX_RESULTS } from 'shared/experiments/consts'
import Button from 'views/components/atoms/Button'
import Form from 'views/components/atoms/Form'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import SearchBoxListItem from 'views/components/organisms/SearchBox/SearchBoxListItem'
import Device from 'views/components/utils/Device'
import Experiment from 'views/components/utils/optimizely/Experiment'
import Tenant from 'views/components/utils/Tenant'
import useMountPoints from 'views/enhancers/useMountPoints'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'
import './SearchBoxList.scss'

const SearchBoxList = ({
  mobile,
  displayResults,
  products,
  redirectData,
  query,
  handleItemClick,
  handleShowAll,
  focusedIndex,
  formAction,
  withHighlightedProduct,
  querySuggestions,
}) => {
  const { tenant, shopURL } = usePublicConfig()
  const mountPoints = useMountPoints()
  const queryStr = query ? query.toLowerCase() : null
  const searchResultsClassname = classnames('u-padding', {
    'o-SearchBox__results': !mobile,
    'o-SearchBox__results--mobile': mobile,
    'u-hidden': !displayResults,
  })
  const handleClick = event => {
    if (handleShowAll) {
      handleShowAll(event)
    }
  }
  const shouldRenderDefaultButton = () => {
    if (mobile) {
      return [TENANT_AT, TENANT_CH].includes(tenant)
    }
    return true
  }

  return (
    <div className={searchResultsClassname} data-qa-id="suggest-list">
      <AfterQuerySuggestLayer
        mountPoints={mountPoints}
        tenant={tenant}
        querySuggestions={querySuggestions}
        redirectData={redirectData}
        shopUrl={shopURL}
      />
      <Device mobile>
        <Tenant tenant={[TENANT_FR]}>
          {() => (
            <Form name="search-results" method="get" action={formAction}>
              <Button
                name="show-all-results-button"
                onClick={handleClick}
                style={!mobile ? 'buttonGhost' : null}
                className="o-SearchBox__show-all-button-top"
              >
                <Trans id="general.label.showAllResults" />
              </Button>
            </Form>
          )}
        </Tenant>
        <Tenant tenant={[TENANT_COM, TENANT_IT]}>
          {() => (
            <Form name="search-results" method="get" action={formAction}>
              <Button
                style="buttonTransparent"
                className="l-flex--justify-content--end"
                name="show-all-results-button"
                onClick={handleClick}
              >
                <TextWithIcon
                  icon={<IconArrowRight />}
                  right
                  iconClassName="u-vertical-align--with-stars"
                >
                  <Trans id="general.label.showAllResults" />
                </TextWithIcon>
              </Button>
            </Form>
          )}
        </Tenant>
      </Device>
      <ul
        className={classnames(
          'a-list-reset o-SearchBox__list u-margin--bottom',
          {
            'o-SearchBox__list--with-highlighted-product':
              withHighlightedProduct,
          }
        )}
      >
        <Experiment preventActivation name={EXTENDED_SEARCHBOX_RESULTS}>
          {({ isEnabled, variant }) => {
            const SearchBoxListItemComponent =
              isEnabled && variant !== 'default'
                ? ExtendedSearchBoxListItem
                : SearchBoxListItem

            return products.map((product, index) => (
              <SearchBoxListItemComponent
                mobile={mobile}
                queryStr={queryStr}
                key={product.id}
                handleItemClick={handleItemClick}
                item={product}
                focused={focusedIndex === index}
                last={index + 1 === products.length}
              />
            ))
          }}
        </Experiment>
      </ul>
      {shouldRenderDefaultButton() && (
        <div className="o-SearchBox__controls u-text-align--center">
          <Form
            className="o-SearchBox__controls__form"
            name="search-results"
            method="get"
            action={formAction}
          >
            <Button
              name="show-all-results-button"
              onClick={handleClick}
              style={!mobile ? 'buttonGhost' : null}
              className="o-SearchBox__show-all-button"
            >
              <Trans id="general.label.showAllResults" />
            </Button>
          </Form>
        </div>
      )}
    </div>
  )
}

SearchBoxList.propTypes = {
  formAction: string.isRequired,
  mobile: bool,
  query: string,
  displayResults: bool,
  products: arrayOf(SearchBoxListItem.propTypes.item),
  redirectData: arrayOf(
    shape({
      redirectTarget: string.isRequired,
      type: string.isRequired,
      suggestText: string,
    })
  ),
  handleShowAll: func,
  handleItemClick: func,
  focusedIndex: number,
  hideResults: func,
  handleValueChange: func,
  searchBannerRef: shape({}),
  withHighlightedProduct: bool,
  querySuggestions: arrayOf(string),
}

SearchBoxList.defaultProps = {
  mobile: false,
  query: '',
  displayResults: false,
  products: [],
  redirectData: [],
  handleShowAll: null,
  handleItemClick: null,
  focusedIndex: -1,
  hideResults: null,
  handleValueChange: null,
  searchBannerRef: null,
  withHighlightedProduct: false,
  querySuggestions: [],
}

export default SearchBoxList
